import type { PhysicalCargoSourceRow } from '../../types/physicalCargoSource';
import { loadingPointToPhysicalSourceKey } from '../routeCreatePhysicalSource/physicalSourceLoadingPoint';
import type { AllocationEdgeDraft, FromStopDraftByUid, QuantityDraftByCargo, ToStopDraftByUid } from './executionDraftTypes';
import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import {
  hydrateLoadingStopFromApplication,
  hydrateStopFromWarehouse,
  hydrateUnloadingStopFromApplication,
  type ApplicationHydrationRef,
  type WarehouseHydrationRef
} from './executionBindingHelpers';
import { resolveGroupApplicationIds } from './deliveryGroupModel';

export const GROUP_MISSING_PHYSICAL_SOURCE_MESSAGE =
  'Выберите физический источник груза (CP-059) для заявки группы перед выбором груза.';

export function getPrimaryLoadingStop(group: DeliveryGroupDraft): DeliveryGroupStopDraft | null {
  return group.loadingStops[0] ?? null;
}

export function isGroupPrimaryPhysicalSourceReady(group: DeliveryGroupDraft): boolean {
  if (group.applicationId == null || !Number.isFinite(Number(group.applicationId))) {
    return false;
  }
  const appId = Number(group.applicationId);
  const primary = getPrimaryLoadingStop(group);
  if (!primary?.pointType || primary.pointId == null) return false;
  if (primary.pointType === 'warehouse') {
    return Number.isFinite(Number(primary.pointId)) && Number(primary.pointId) > 0;
  }
  if (primary.pointType === 'application') {
    return Number(primary.pointId) === appId;
  }
  return false;
}

export function primaryLoadingStopMatchesPhysicalSourcesList(
  group: DeliveryGroupDraft,
  sources: PhysicalCargoSourceRow[]
): boolean {
  const primary = getPrimaryLoadingStop(group);
  if (!primary?.pointType || primary.pointId == null) return false;
  const key = loadingPointToPhysicalSourceKey({
    applicationId: group.applicationId,
    loadingPointType: primary.pointType,
    loadingPointId: primary.pointId
  });
  if (!key) return false;
  return sources.some(
    (s) => `${s.sourceType}:${s.sourceId}` === key && s.cargoCount > 0 && s.availableQuantitySum > 0
  );
}

export function evaluateGroupCargoStepEntryGate(
  groups: DeliveryGroupDraft[],
  options?: {
    membershipForGroup?: (
      group: DeliveryGroupDraft
    ) => { permitted: boolean; message?: string };
  }
): { ok: true } | { ok: false; message: string } {
  for (let index = 0; index < groups.length; index++) {
    const group = groups[index];
    const appIds = resolveGroupApplicationIds(group);
    const hasWarehouseLoading = group.loadingStops.some((s) => s.pointType === 'warehouse' && s.pointId);
    if (appIds.size === 0 && hasWarehouseLoading) {
      return {
        ok: false,
        message: 'Склад погрузки без заявки недопустим (RC-1). Выберите заявку для группы.'
      };
    }
    if (group.applicationId && !isGroupPrimaryPhysicalSourceReady(group)) {
      const label = group.label || `группа ${index + 1}`;
      return {
        ok: false,
        message: `${GROUP_MISSING_PHYSICAL_SOURCE_MESSAGE} (${label})`
      };
    }
    if (
      group.applicationId &&
      isGroupPrimaryPhysicalSourceReady(group) &&
      options?.membershipForGroup
    ) {
      const membership = options.membershipForGroup(group);
      if (!membership.permitted) {
        const label = group.label || `группа ${index + 1}`;
        return {
          ok: false,
          message: membership.message
            ? `${membership.message} (${label})`
            : `${GROUP_MISSING_PHYSICAL_SOURCE_MESSAGE} (${label})`
        };
      }
    }
  }
  return { ok: true };
}

function emptyPrimaryLoadingDraft(groupUid: string, makeStopUid: () => string): DeliveryGroupStopDraft {
  return {
    uid: makeStopUid(),
    groupUid,
    type: 'loading',
    pointType: null,
    pointId: null,
    address: null,
    contactPerson: null,
    phone: null,
    plannedAt: null,
    actualAt: null,
    notes: null,
    status: null
  };
}

/** Application change — clear primary physical source; keep group shell and unloading hydration. */
export function applyGroupApplicationChange(params: {
  group: DeliveryGroupDraft;
  applicationId: number | null;
  application?: ApplicationHydrationRef;
  makeStopUid: () => string;
}): DeliveryGroupDraft {
  const { group, applicationId, application, makeStopUid } = params;

  let loadingStops = [...group.loadingStops];
  if (loadingStops.length === 0) {
    loadingStops = [emptyPrimaryLoadingDraft(group.uid, makeStopUid)];
  } else {
    loadingStops = loadingStops.map((s, idx) =>
      idx === 0
        ? {
            ...s,
            pointType: null,
            pointId: null,
            address: null,
            contactPerson: null,
            phone: null
          }
        : s
    );
  }

  let unloadingStops = group.unloadingStops;
  if (application) {
    const emptyUnload: DeliveryGroupStopDraft = {
      uid: makeStopUid(),
      groupUid: group.uid,
      type: 'unloading',
      pointType: null,
      pointId: null,
      address: null,
      contactPerson: null,
      phone: null,
      plannedAt: null,
      actualAt: null,
      notes: null,
      status: null
    };
    if (unloadingStops.length === 0) {
      unloadingStops = [hydrateUnloadingStopFromApplication(emptyUnload, application)];
    } else {
      unloadingStops = unloadingStops.map((s, idx) =>
        idx === 0 &&
        (s.pointType === 'application' || s.pointType === 'consignee' || s.pointType == null)
          ? hydrateUnloadingStopFromApplication(
              { ...s, pointType: 'application', pointId: application.id },
              application
            )
          : s
      );
    }
  }

  return {
    ...group,
    applicationId,
    loadingStops,
    unloadingStops
  };
}

export function applyGroupPhysicalSourceSelection(params: {
  group: DeliveryGroupDraft;
  loadingPointType: 'application' | 'warehouse';
  loadingPointId: number;
  applicationId: number;
  application?: ApplicationHydrationRef;
  warehouse?: WarehouseHydrationRef;
  makeStopUid: () => string;
}): DeliveryGroupDraft {
  const { group, application, warehouse, makeStopUid } = params;
  let loadingStops = [...group.loadingStops];
  if (loadingStops.length === 0) {
    loadingStops = [emptyPrimaryLoadingDraft(group.uid, makeStopUid)];
  }

  const primary = loadingStops[0];
  let nextPrimary: DeliveryGroupStopDraft;
  if (params.loadingPointType === 'application' && application) {
    nextPrimary = hydrateLoadingStopFromApplication(
      { ...primary, pointType: 'application', pointId: application.id },
      application
    );
  } else if (params.loadingPointType === 'warehouse' && warehouse) {
    nextPrimary = hydrateStopFromWarehouse(
      { ...primary, pointType: 'warehouse', pointId: params.loadingPointId },
      warehouse,
      true
    );
  } else {
    nextPrimary = {
      ...primary,
      pointType: params.loadingPointType,
      pointId: params.loadingPointId
    };
  }

  loadingStops = [nextPrimary, ...loadingStops.slice(1)];

  return {
    ...group,
    applicationId: params.applicationId,
    loadingStops
  };
}

export function collectGroupLoadingStopUids(
  group: DeliveryGroupDraft,
  groupUid?: string
): Set<string> {
  if (groupUid && group.uid !== groupUid) return new Set();
  return new Set(group.loadingStops.map((s) => s.uid));
}

export function clearExecutionDraftForGroup(params: {
  groupUid: string;
  groups: DeliveryGroupDraft[];
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  toStopDraft: ToStopDraftByUid;
  allocationEdges?: AllocationEdgeDraft[];
}): {
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  toStopDraft: ToStopDraftByUid;
  allocationEdges: AllocationEdgeDraft[];
} {
  const group = params.groups.find((g) => g.uid === params.groupUid);
  if (!group) {
    return {
      selectedIds: params.selectedIds,
      fromStopDraft: params.fromStopDraft,
      quantityDraft: params.quantityDraft,
      toStopDraft: params.toStopDraft,
      allocationEdges: params.allocationEdges ?? []
    };
  }

  const loadingUids = collectGroupLoadingStopUids(group);
  const cargoIdsToClear = new Set<number>();

  for (const [cargoKey, fromUid] of Object.entries(params.fromStopDraft)) {
    const cargoId = Number(cargoKey);
    if (!Number.isFinite(cargoId)) continue;
    if (fromUid && loadingUids.has(fromUid)) {
      cargoIdsToClear.add(cargoId);
    }
  }

  for (const edge of params.allocationEdges ?? []) {
    if (edge.fromStopUid && loadingUids.has(edge.fromStopUid) && edge.quantity > 0) {
      cargoIdsToClear.add(Number(edge.cargoId));
    }
  }

  const selectedIds = params.selectedIds.filter((id) => !cargoIdsToClear.has(Number(id)));
  const fromStopDraft = { ...params.fromStopDraft };
  const quantityDraft = { ...params.quantityDraft };
  const toStopDraft = { ...params.toStopDraft };
  Array.from(cargoIdsToClear).forEach((id) => {
    delete fromStopDraft[id];
    delete quantityDraft[id];
    delete toStopDraft[id];
  });

  const allocationEdges = (params.allocationEdges ?? []).filter(
    (e) => !(e.fromStopUid && loadingUids.has(e.fromStopUid))
  );

  return { selectedIds, fromStopDraft, quantityDraft, toStopDraft, allocationEdges };
}
